import { Skeleton, message as antdMessage } from "antd"

import { useQuery } from "@tanstack/react-query"
import { ReactNode, useCallback, useEffect, useState } from "react"
import { useTheme } from "styled-components"
import { ContainerFull, Steps2 } from "../../components"
import {
    ChooseService,
    EnterSampleInformationAmpliconSeq,
    EnterSampleInformationPlasmidQC,
    EnterSampleInformationPlasmidSeq,
    EnterSampleInformationPolyaQC,
    EnterSampleInformationRcaQC,
    PreviewOrder,
} from "../../components/order"
import { ShowMessagePage } from "../../config"
import { getOrders, getProducts } from "../../endpoints"
import { OrderInfoInterface } from "../../types"

const StepsInitialValues = [
    {
        title: "Choose Service",
    },
    {
        title: "Enter Sample Information",
    },
    {
        title: "Submit",
    },
]

//todo: get last order information and use that to initialize the order
// eslint-disable-next-line
const orderInfoInitialValue: OrderInfoInterface = {
    user: {
        id: 0,
        firstName: "",
        lastName: "",
        email: "",
        userRole: "customer",
    },
    productid: null,
    concentration: undefined,
    specialNotes: "",
    ordername: "",
    samples: [],
    sequenceupload: undefined,
    shippinginfo: null,
    paymentInfo: null,
    status: "new",
}

export function PlaceOrder(): JSX.Element {
    const [currentStep, setCurrentStep] = useState(0) //current step
    const [orderInfo, setOrderInfo] = useState<OrderInfoInterface>(orderInfoInitialValue)
    const [messageApi, contextHolder] = antdMessage.useMessage()
    const [stepItems, setStepItems] =
        useState<{ title: string; description?: ReactNode; icon?: ReactNode }[]>(StepsInitialValues)
    const theme = useTheme()
    const { isPending, isError, data, error, isSuccess } = useQuery({
        queryKey: ["getproducts"],
        queryFn: getProducts,
        // refetchOnMount:false
    })
    useEffect(() => {
        // Fetch the last order for the user
        // console.log("first useEffect, orderInfo is ", orderInfo, orderInfo.shippinginfo===null)
        if (orderInfo.shippinginfo === null) {
            getOrders({ type: "one", orderIds: [-1] }).then((res) => {
                // console.log("getOrders res is ", res)
                if (res.code === 1000 && Array.isArray(res.orders) && res.orders.length > 0) {
                    const lastOrder = res.orders[0]
                    // console.log("last order is ", lastOrder)
                    setOrderInfo((prev) => {
                        const newOrderInfo = { ...prev }
                        if (lastOrder?.paymentInfo?.method === "PO" && lastOrder?.paymentInfo?.PoNumber?.toLowerCase()?.includes('free order')) {
                            lastOrder.paymentInfo.PoNumber = ""
                        }
                        newOrderInfo.shippinginfo = lastOrder.shippinginfo || null
                        newOrderInfo.paymentInfo = lastOrder.paymentInfo || null
                        return newOrderInfo
                    })
                }
            })
        }
    }, [orderInfo.shippinginfo])
    
    // Separate useEffect to update productDetails and concentration when product is selected
    useEffect(() => {
        if (orderInfo.productid !== null && data?.products) {
            setOrderInfo((prev) => {
                const newOrderInfo = { ...prev }
                newOrderInfo.productDetails = data.products.find((x) => x.id === orderInfo.productid)
                newOrderInfo.concentration = newOrderInfo.productDetails?.defaultConcentration
                return newOrderInfo
            })
        }
    }, [orderInfo.productid, data?.products])
    
    useEffect(() => {
        // console.log('called useEffect to update stepItems')
        // console.log('orderInfo is ', orderInfo)
        setStepItems((items) => {
            const newItems = []
            for (let i = 0; i < items.length; i++) {
                const item2 = { ...items[i] }
                // console.log("current step", currentStep)
                // console.log(orderInfo.samples)
                // console.log(item2)
                const sampleNames = orderInfo.samples.map((o) => o.sampleName)
                if (orderInfo.productid !== null) {
                    if (item2.title === "Choose Service") {
                        item2.description = (
                            <div style={{ color: theme.secondary }}>
                                {data?.products.find((x) => x.id === orderInfo.productid)?.name}
                            </div>
                        )
                    }
                    if (sampleNames.length > 0) {
                        // console.log("herer  ")
                        if (item2.title === "Enter Sample Information") {
                            item2.description = (
                                <div style={{ color: theme.secondary }}>{`${sampleNames.length} samples entered`}</div>
                            )
                        }
                    }else{
                        if (item2.title === "Enter Sample Information") {
                            item2.description = (
                                    <></>
                                )
                        }
                    }
                }
                newItems.push(item2)
            }
            return newItems
        })
    }, [orderInfo.samples, orderInfo.productid, data?.products, theme.secondary])
    const onStepsChange = useCallback(
        (value: number) => {
            // console.log("click step", value)
            switch (value) {
                case 0:
                    setCurrentStep(value)
                    // Scroll to top when changing steps
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                    break
                default:
                    if (orderInfo.productid != null) {
                        if (value === 2 && orderInfo.samples.length === 0) {
                            messageApi.warning("Please add samples to proceed with the order.")
                            return
                        } else {
                            setCurrentStep(value)
                            // Scroll to top when changing steps
                            window.scrollTo({ top: 0, behavior: 'smooth' })
                        }
                    } else {
                        messageApi.warning("Please choose product to proceed with the order.")
                        return
                    }
                    break
            }
        },
        [orderInfo.productid, orderInfo.samples, messageApi]
    )
    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error.message)
    }
    if (isSuccess) {
    }
    // console.log(orderInfo)
    return (
        <div>
            {contextHolder}
            <ContainerFull style={{ marginTop: "3em", marginBottom: "2em" }}>
                <Steps2 current={currentStep} onChange={onStepsChange} items={stepItems} />
            </ContainerFull>
            {currentStep === 0 && (
                <ChooseService orderInfo={orderInfo} setOrderInfo={setOrderInfo} setCurrentStep={setCurrentStep} />
            )}
            {/* <h1>{orderInfo.productid+'service'}</h1> */}
            {currentStep === 1 && (orderInfo.productid === -6 ? (
                <EnterSampleInformationPlasmidSeq
                    orderInfo={orderInfo}
                    setOrderInfo={setOrderInfo}
                    setCurrentStep={setCurrentStep}
                    enterSampleNames={true}
                />
            ) : (orderInfo.productid === -1 ? (
                <EnterSampleInformationAmpliconSeq
                    orderInfo={orderInfo}
                    setOrderInfo={setOrderInfo}
                    setCurrentStep={setCurrentStep}
                    enterSampleNames={true}
                />
            ) : (orderInfo.productid === 2 ? (
                <EnterSampleInformationPlasmidQC
                    orderInfo={orderInfo}
                    setOrderInfo={setOrderInfo}
                    setCurrentStep={setCurrentStep}
                    enterSampleNames={true}
                />
            ) : (orderInfo.productid === 7 ? (
                <EnterSampleInformationPolyaQC
                    orderInfo={orderInfo}
                    setOrderInfo={setOrderInfo}
                    setCurrentStep={setCurrentStep}
                    
                />
            ) : (orderInfo.productid === 6 ? (
                <EnterSampleInformationRcaQC
                    orderInfo={orderInfo}
                    setOrderInfo={setOrderInfo}
                    setCurrentStep={setCurrentStep}
                    
                />
            ) : <div />)))))
            }

            {orderInfo.productid !== null && currentStep === 2 && (
                <PreviewOrder orderInfo={orderInfo} setOrderInfo={setOrderInfo} setCurrentStep={setCurrentStep} />
            )}
        </div>
    )
}
